import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, Flame, Wine, Coffee } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
interface MenuItem {
  name: string;
  price: string;
  description: string;
  dietary?: string[];
  spicy?: boolean;
}
const menuData = {
  appetizers: [{
    name: 'Oysters Rockefeller',
    price: '$24',
    description: 'Half dozen local oysters, spinach, pernod, parmesan gratin',
    dietary: ['GF']
  }, {
    name: 'Grilled Octopus',
    price: '$28',
    description: 'Charred octopus, romesco sauce, fingerling potatoes, chorizo oil',
    dietary: ['GF']
  }, {
    name: 'Crab Cakes',
    price: '$22',
    description: 'Jumbo lump crab, remoulade, microgreens, lemon'
  }, {
    name: 'Tuna Tartare',
    price: '$26',
    description: 'Yellowfin tuna, avocado, sesame, soy ginger dressing, wonton crisps'
  }, {
    name: 'Clam Chowder',
    price: '$14',
    description: 'New England style, bacon, potatoes, fresh herbs, oyster crackers'
  }, {
    name: 'Calamari Fritti',
    price: '$18',
    description: 'Crispy squid, cherry peppers, marinara, lemon aioli',
    spicy: true
  }],
  mains: [{
    name: 'The Dock Tower',
    price: '$120',
    description: 'Lobster tail, king crab legs, oysters, prawns, mussels, house sauces',
    dietary: ['GF']
  }, {
    name: 'Pan-Seared Scallops',
    price: '$34',
    description: 'Hand-dived scallops, cauliflower purée, crispy pancetta, truffle oil',
    dietary: ['GF']
  }, {
    name: 'Blackened Halibut',
    price: '$42',
    description: 'Cajun spiced halibut, mango salsa, coconut rice, lime beurre blanc',
    dietary: ['GF'],
    spicy: true
  }, {
    name: 'Squid Ink Linguine',
    price: '$32',
    description: 'Fresh pasta, clams, chili, garlic, white wine, parsley',
    spicy: true
  }, {
    name: 'Grilled Swordfish',
    price: '$38',
    description: 'Mediterranean style, olive tapenade, roasted vegetables, herb oil',
    dietary: ['GF']
  }, {
    name: 'Lobster Risotto',
    price: '$46',
    description: 'Creamy arborio rice, fresh lobster, peas, parmesan, tarragon',
    dietary: ['GF', 'V']
  }, {
    name: 'Whole Grilled Branzino',
    price: '$44',
    description: 'Mediterranean sea bass, lemon, herbs, roasted fingerlings',
    dietary: ['GF']
  }, {
    name: 'Surf & Turf',
    price: '$68',
    description: '8oz filet mignon, lobster tail, truffle mash, asparagus, red wine jus',
    dietary: ['GF']
  }],
  desserts: [{
    name: 'Key Lime Pie',
    price: '$12',
    description: 'Classic Florida style, graham cracker crust, whipped cream',
    dietary: ['V']
  }, {
    name: 'Chocolate Lava Cake',
    price: '$14',
    description: 'Warm molten center, vanilla ice cream, berry compote',
    dietary: ['V']
  }, {
    name: 'Crème Brûlée',
    price: '$13',
    description: 'Vanilla bean custard, caramelized sugar, fresh berries',
    dietary: ['GF', 'V']
  }, {
    name: 'Tiramisu',
    price: '$13',
    description: 'Espresso soaked ladyfingers, mascarpone, cocoa',
    dietary: ['V']
  }, {
    name: 'Seasonal Sorbet Trio',
    price: '$10',
    description: 'Three scoops of house-made sorbet, fresh mint',
    dietary: ['GF', 'V', 'VG']
  }],
  beverages: [{
    name: 'House Wine Selection',
    price: '$12-18',
    description: 'Red, white, rosé by the glass'
  }, {
    name: 'Craft Cocktails',
    price: '$16-20',
    description: 'Signature maritime-inspired cocktails'
  }, {
    name: 'Local Draft Beer',
    price: '$8-12',
    description: 'Rotating selection of regional brews'
  }, {
    name: 'Espresso & Coffee',
    price: '$5-8',
    description: 'Freshly brewed, cappuccino, latte, americano'
  }, {
    name: 'Fresh Juices',
    price: '$6',
    description: 'Orange, grapefruit, cranberry, pineapple'
  }]
};
const dietaryIcons: {
  [key: string]: {
    label: string;
    color: string;
  };
} = {
  GF: {
    label: 'Gluten Free',
    color: 'text-green-500'
  },
  V: {
    label: 'Vegetarian',
    color: 'text-blue-500'
  },
  VG: {
    label: 'Vegan',
    color: 'text-purple-500'
  }
};
export function MenuPage() {
  const renderMenuSection = (title: string, items: MenuItem[], icon: React.ReactNode) => <motion.section initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5
  }} className="mb-20" data-id="element-320">
      <div className="flex items-center gap-3 mb-8" data-id="element-321">
        <div className="text-gold-500" data-id="element-322">{icon}</div>
        <h2 className="font-heading font-bold text-3xl md:text-4xl text-white" data-id="element-323">
          {title}
        </h2>
        <div className="flex-grow h-px bg-gradient-to-r from-gold-500/50 to-transparent" data-id="element-324" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-325">
        {items.map((item, index) => <motion.div key={index} initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: index * 0.05,
        duration: 0.3
      }} className="bg-navy-800/50 border border-navy-700 p-6 hover:border-ocean-500/50 transition-all duration-300" data-id="element-326">
            <div className="flex justify-between items-start mb-2" data-id="element-327">
              <h3 className="font-heading font-bold text-xl text-white flex items-center gap-2" data-id="element-328">
                {item.name}
                {item.spicy && <Flame className="w-4 h-4 text-red-500" title="Spicy" data-id="element-329" />}
              </h3>
              <span className="font-heading font-bold text-xl text-gold-500 whitespace-nowrap ml-4" data-id="element-330">
                {item.price}
              </span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-3" data-id="element-331">
              {item.description}
            </p>
            {item.dietary && item.dietary.length > 0 && <div className="flex gap-2 flex-wrap" data-id="element-332">
                {item.dietary.map(diet => <span key={diet} className={`text-xs font-bold uppercase tracking-wider ${dietaryIcons[diet]?.color || 'text-gray-500'}`} title={dietaryIcons[diet]?.label} data-id="element-333">
                    {diet}
                  </span>)}
              </div>}
          </motion.div>)}
      </div>
    </motion.section>;
  return <div className="min-h-screen bg-navy-900 text-white selection:bg-gold-500 selection:text-navy-900 flex flex-col" data-id="element-334">
      <Navigation data-id="element-335" />

      <main className="flex-grow pt-24 pb-16" data-id="element-336">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-337">
          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="text-center mb-16" data-id="element-338">
            <h1 className="font-heading font-bold text-4xl md:text-6xl text-white mb-4" data-id="element-339">
              Our <span className="text-ocean-500" data-id="element-340">Menu</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8" data-id="element-341">
              Fresh catches and coastal cuisine, crafted daily by our culinary
              team. All seafood is sustainably sourced from local waters.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm" data-id="element-342">
              <div className="flex items-center gap-2" data-id="element-343">
                <Leaf className="w-4 h-4 text-green-500" data-id="element-344" />
                <span className="text-gray-400" data-id="element-345">
                  <span className="text-green-500 font-bold" data-id="element-346">GF</span> Gluten
                  Free
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-347">
                <Leaf className="w-4 h-4 text-blue-500" data-id="element-348" />
                <span className="text-gray-400" data-id="element-349">
                  <span className="text-blue-500 font-bold" data-id="element-350">V</span> Vegetarian
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-351">
                <Leaf className="w-4 h-4 text-purple-500" data-id="element-352" />
                <span className="text-gray-400" data-id="element-353">
                  <span className="text-purple-500 font-bold" data-id="element-354">VG</span> Vegan
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-355">
                <Flame className="w-4 h-4 text-red-500" data-id="element-356" />
                <span className="text-gray-400" data-id="element-357">Spicy</span>
              </div>
            </div>
          </motion.div>

          {/* Menu Sections */}
          {renderMenuSection('Appetizers', menuData.appetizers, <Leaf className="w-8 h-8" data-id="element-358" />)}
          {renderMenuSection('Main Courses', menuData.mains, <Flame className="w-8 h-8" data-id="element-359" />)}
          {renderMenuSection('Desserts', menuData.desserts, <Coffee className="w-8 h-8" data-id="element-360" />)}
          {renderMenuSection('Beverages', menuData.beverages, <Wine className="w-8 h-8" data-id="element-361" />)}

          {/* Footer Note */}
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="mt-16 text-center bg-navy-800/50 border border-navy-700 p-8" data-id="element-362">
            <p className="text-gray-400 mb-4" data-id="element-363">
              Menu items and prices are subject to change based on seasonal
              availability.
            </p>
            <p className="text-gray-400" data-id="element-364">
              Please inform your server of any dietary restrictions or
              allergies.
            </p>
          </motion.div>
        </div>
      </main>

      <Footer data-id="element-365" />
    </div>;
}